import React, { useState, useEffect, useMemo } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { projectService } from '../../services/projectService';
import { bidService } from '../../services/bidService';
import { resourceService } from '../../services/resourceService';

const Archive = () => {
  const [activeTab, setActiveTab] = useState('projects');

  // Projects state
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(null);
  const [unarchivingProjectId, setUnarchivingProjectId] = useState(null);

  // Restore confirmation state
  const [restoreConfirm, setRestoreConfirm] = useState(null); // project to restore
  const [restoringProjectId, setRestoringProjectId] = useState(null);

  // Unassign resources state
  const [unassignConfirm, setUnassignConfirm] = useState(null); // project to unassign resources from
  const [unassigningProjectId, setUnassigningProjectId] = useState(null);

  // Bids state
  const [archivedBids, setArchivedBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(true);
  const [bidsError, setBidsError] = useState(null);
  const [unarchivingBidId, setUnarchivingBidId] = useState(null);

  // Shared filter/sort state
  const [projectSearch, setProjectSearch] = useState('');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const [projectSort, setProjectSort] = useState({ column: 'updatedAt', direction: 'desc' });

  const [bidSearch, setBidSearch] = useState('');
  const [bidStatusFilter, setBidStatusFilter] = useState('all');
  const [bidSort, setBidSort] = useState({ column: 'updatedAt', direction: 'desc' });

  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadArchivedProjects();
    loadArchivedBids();
  }, []);

  const loadArchivedProjects = async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const { data, error } = await projectService?.getArchived();
      if (error) throw error;
      setArchivedProjects(data || []);
    } catch (err) {
      setProjectsError(err?.message || 'Failed to load archived projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadArchivedBids = async () => {
    setBidsLoading(true);
    setBidsError(null);
    try {
      const { data, error } = await bidService?.getArchived();
      if (error) throw error;
      setArchivedBids(data || []);
    } catch (err) {
      setBidsError(err?.message || 'Failed to load archived bids');
    } finally {
      setBidsLoading(false);
    }
  };

  const handleUnarchiveProject = async (project) => {
    setUnarchivingProjectId(project?.id);
    try {
      const { error } = await projectService?.unarchive(project?.id, project?.preArchiveStatus);
      if (error) throw error;
      await loadArchivedProjects();
      showSuccess(`Project "${project?.projectName}" has been unarchived.`);
    } catch (err) {
      console.error('Error unarchiving project:', err?.message);
    } finally {
      setUnarchivingProjectId(null);
    }
  };

  const handleUnarchiveBid = async (bid) => {
    setUnarchivingBidId(bid?.id);
    try {
      const { error } = await bidService?.unarchive(bid?.id);
      if (error) throw error;
      await loadArchivedBids();
      showSuccess(`Bid "${bid?.bidNumber}" has been unarchived.`);
    } catch (err) {
      console.error('Error unarchiving bid:', err?.message);
    } finally {
      setUnarchivingBidId(null);
    }
  };

  const handleRestoreProject = async () => {
    if (!restoreConfirm) return;
    const project = restoreConfirm;
    setRestoreConfirm(null);
    setRestoringProjectId(project?.id);
    try {
      const { error } = await projectService?.restore(project?.id);
      if (error) throw error;
      await loadArchivedProjects();
      showSuccess(`Project "${project?.projectName}" has been restored to Active status.`);
    } catch (err) {
      console.error('Error restoring project:', err?.message);
    } finally {
      setRestoringProjectId(null);
    }
  };

  const handleUnassignResources = async () => {
    if (!unassignConfirm) return;
    const project = unassignConfirm;
    setUnassignConfirm(null);
    setUnassigningProjectId(project?.id);
    try {
      const { error } = await resourceService?.unassignAllFromProject(project?.id);
      if (error) throw error;
      showSuccess(`All resources have been unassigned from "${project?.projectName}".`);
    } catch (err) {
      console.error('Error unassigning resources:', err?.message);
    } finally {
      setUnassigningProjectId(null);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(amount);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = String(dateString)?.split('T')?.[0]?.split('-');
    if (parts?.length !== 3) return dateString;
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  };

  // Filtered & sorted projects
  const filteredProjects = useMemo(() => {
    let result = [...archivedProjects];
    if (projectSearch) {
      const q = projectSearch?.toLowerCase();
      result = result?.filter(
        (p) =>
          p?.projectNumber?.toLowerCase()?.includes(q) ||
          p?.projectName?.toLowerCase()?.includes(q) ||
          p?.customerName?.toLowerCase()?.includes(q)
      );
    }
    if (projectStatusFilter !== 'all') {
      result = result?.filter((p) => p?.preArchiveStatus === projectStatusFilter || p?.status === projectStatusFilter);
    }
    result?.sort((a, b) => {
      let aVal, bVal;
      switch (projectSort?.column) {
        case 'projectNumber': aVal = a?.projectNumber || ''; bVal = b?.projectNumber || ''; break;
        case 'projectName': aVal = a?.projectName || ''; bVal = b?.projectName || ''; break;
        case 'customerName': aVal = a?.customerName || ''; bVal = b?.customerName || ''; break;
        case 'status': aVal = a?.preArchiveStatus || a?.status || ''; bVal = b?.preArchiveStatus || b?.status || ''; break;
        case 'budget': aVal = parseFloat(a?.totalBudget) || 0; bVal = parseFloat(b?.totalBudget) || 0; break;
        case 'updatedAt': aVal = new Date(a?.updatedAt || a?.createdAt); bVal = new Date(b?.updatedAt || b?.createdAt); break;
        default: aVal = new Date(a?.updatedAt || a?.createdAt); bVal = new Date(b?.updatedAt || b?.createdAt);
      }
      if (typeof aVal === 'string') {
        return projectSort?.direction === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      }
      return projectSort?.direction === 'asc' ? (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) : (bVal < aVal ? -1 : bVal > aVal ? 1 : 0);
    });
    return result;
  }, [archivedProjects, projectSearch, projectStatusFilter, projectSort]);

  // Filtered & sorted bids
  const filteredBids = useMemo(() => {
    let result = [...archivedBids];
    if (bidSearch) {
      const q = bidSearch?.toLowerCase();
      result = result?.filter(
        (b) =>
          b?.bidNumber?.toLowerCase()?.includes(q) ||
          b?.customerName?.toLowerCase()?.includes(q) ||
          b?.projectName?.toLowerCase()?.includes(q)
      );
    }
    if (bidStatusFilter !== 'all') {
      result = result?.filter((b) => b?.preArchiveStatus === bidStatusFilter || b?.status === bidStatusFilter);
    }
    result?.sort((a, b) => {
      let aVal, bVal;
      switch (bidSort?.column) {
        case 'bidNumber': aVal = a?.bidNumber || ''; bVal = b?.bidNumber || ''; break;
        case 'customerName': aVal = a?.customerName || ''; bVal = b?.customerName || ''; break;
        case 'projectName': aVal = a?.projectName || ''; bVal = b?.projectName || ''; break;
        case 'totalAmount': aVal = parseFloat(a?.totalAmount) || 0; bVal = parseFloat(b?.totalAmount) || 0; break;
        case 'status': aVal = a?.preArchiveStatus || a?.status || ''; bVal = b?.preArchiveStatus || b?.status || ''; break;
        case 'updatedAt': aVal = new Date(a?.updatedAt || a?.createdAt); bVal = new Date(b?.updatedAt || b?.createdAt); break;
        default: aVal = new Date(a?.updatedAt || a?.createdAt); bVal = new Date(b?.updatedAt || b?.createdAt);
      }
      if (typeof aVal === 'string') {
        return bidSort?.direction === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      }
      return bidSort?.direction === 'asc' ? (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) : (bVal < aVal ? -1 : bVal > aVal ? 1 : 0);
    });
    return result;
  }, [archivedBids, bidSearch, bidStatusFilter, bidSort]);

  const handleProjectSort = (col) => {
    setProjectSort((prev) =>
      prev?.column === col
        ? { column: col, direction: prev?.direction === 'asc' ? 'desc' : 'asc' }
        : { column: col, direction: 'asc' }
    );
  };

  const handleBidSort = (col) => {
    setBidSort((prev) =>
      prev?.column === col
        ? { column: col, direction: prev?.direction === 'asc' ? 'desc' : 'asc' }
        : { column: col, direction: 'asc' }
    );
  };

  const SortIcon = ({ col, currentSort }) => {
    if (currentSort?.column !== col) return <Icon name="ChevronsUpDown" size={13} className="text-muted-foreground/50 ml-1" />;
    return currentSort?.direction === 'asc'
      ? <Icon name="ChevronUp" size={13} className="text-accent ml-1" />
      : <Icon name="ChevronDown" size={13} className="text-accent ml-1" />;
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      'Planning': 'bg-blue-100 text-blue-700',
      'In Progress': 'bg-yellow-100 text-yellow-700',
      'On Hold': 'bg-orange-100 text-orange-700',
      'Completed': 'bg-green-100 text-green-700',
      'Cancelled': 'bg-red-100 text-red-700',
      'Draft': 'bg-gray-100 text-gray-600',
      'Submitted': 'bg-blue-100 text-blue-700',
      'Accepted': 'bg-green-100 text-green-700',
      'Rejected': 'bg-red-100 text-red-700',
      'Converted to Project': 'bg-purple-100 text-purple-700',
      'Converted to a Project': 'bg-purple-100 text-purple-700',
    };
    const cls = colors?.[status] || 'bg-muted text-muted-foreground';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
        {status || '—'}
      </span>
    );
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Restore Confirmation Dialog */}
        {restoreConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-card rounded-xl border border-border shadow-xl w-full max-w-md p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="RotateCcw" size={18} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-1">Restore Project</h3>
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to restore <span className="font-medium text-foreground">"{restoreConfirm?.projectName}"</span> to Active status? This will move it back to the main project list as <span className="font-medium text-foreground">In Progress</span>.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setRestoreConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRestoreProject}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Icon name="RotateCcw" size={14} />
                  Restore to Active
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unassign Resources Confirmation Dialog */}
        {unassignConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-card rounded-xl border border-border shadow-xl w-full max-w-md p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="UserMinus" size={18} className="text-orange-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-1">Unassign All Resources</h3>
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to unassign all resources from <span className="font-medium text-foreground">"{unassignConfirm?.projectName}"</span>? This will remove all resource assignments from this project.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setUnassignConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnassignResources}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Icon name="UserMinus" size={14} />
                  Unassign Resources
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">Archive</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            View and manage archived projects and bids. Unarchive items to restore them to the main screens.
          </p>
        </div>

        {/* Success toast */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            <Icon name="CheckCircle2" size={16} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex gap-0">
            {[
              { id: 'projects', label: 'Projects', icon: 'Briefcase', count: archivedProjects?.length },
              { id: 'bids', label: 'Bids', icon: 'FileText', count: archivedBids?.length },
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab?.id ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'}`}>
                  {tab?.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                <div className="relative flex-1 min-w-0">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search archived projects..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e?.target?.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                  />
                </div>
                <select
                  value={projectStatusFilter}
                  onChange={(e) => setProjectStatusFilter(e?.target?.value)}
                  className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                >
                  <option value="all">All Statuses</option>
                  <option value="Planning">Planning</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                {(projectSearch || projectStatusFilter !== 'all') && (
                  <button
                    onClick={() => { setProjectSearch(''); setProjectStatusFilter('all'); }}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors whitespace-nowrap"
                  >
                    <Icon name="X" size={14} />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Projects Table */}
            {projectsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={24} className="animate-spin text-accent mr-2" />
                <span className="text-muted-foreground">Loading archived projects...</span>
              </div>
            ) : projectsError ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{projectsError}</span>
                <button onClick={loadArchivedProjects} className="ml-auto underline text-xs">Retry</button>
              </div>
            ) : filteredProjects?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Icon name="Archive" size={24} className="text-muted-foreground" />
                </div>
                <h3 className="text-base font-medium text-foreground mb-1">No archived projects</h3>
                <p className="text-sm text-muted-foreground">
                  {projectSearch || projectStatusFilter !== 'all' ?'No projects match your filters.' :'Projects you archive will appear here.'}
                </p>
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        {[
                          { key: 'projectNumber', label: 'Project #' },
                          { key: 'projectName', label: 'Project Name' },
                          { key: 'customerName', label: 'Customer' },
                          { key: 'status', label: 'Status at Archive' },
                          { key: 'budget', label: 'Budget' },
                          { key: 'updatedAt', label: 'Archived On' },
                        ]?.map((col) => (
                          <th key={col?.key} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            <button
                              onClick={() => handleProjectSort(col?.key)}
                              className="flex items-center hover:text-foreground transition-colors"
                            >
                              {col?.label}
                              <SortIcon col={col?.key} currentSort={projectSort} />
                            </button>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredProjects?.map((project) => (
                        <tr key={project?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap">
                            {project?.projectNumber}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground">
                            {project?.projectName}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {project?.customerName}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={project?.preArchiveStatus || project?.status} />
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {project?.totalBudget ? formatCurrency(project?.totalBudget) : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(project?.updatedAt || project?.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {(project?.status === 'Cancelled' || project?.preArchiveStatus === 'Cancelled') && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setRestoreConfirm(project)}
                                  disabled={restoringProjectId === project?.id}
                                  iconName={restoringProjectId === project?.id ? undefined : 'RotateCcw'}
                                  iconPosition="left"
                                  iconSize={14}
                                  className="text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
                                >
                                  {restoringProjectId === project?.id ? 'Restoring...' : 'Restore'}
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setUnassignConfirm(project)}
                                disabled={unassigningProjectId === project?.id}
                                iconName={unassigningProjectId === project?.id ? undefined : 'UserMinus'}
                                iconPosition="left"
                                iconSize={14}
                                className="text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300"
                              >
                                {unassigningProjectId === project?.id ? 'Unassigning...' : 'Unassign Resources'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleUnarchiveProject(project)}
                                disabled={unarchivingProjectId === project?.id}
                                iconName={unarchivingProjectId === project?.id ? undefined : 'ArchiveRestore'}
                                iconPosition="left"
                                iconSize={14}
                              >
                                {unarchivingProjectId === project?.id ? 'Restoring...' : 'Unarchive'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2 border-t border-border bg-muted/20">
                  <p className="text-xs text-muted-foreground">
                    {filteredProjects?.length} archived project{filteredProjects?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bids Tab */}
        {activeTab === 'bids' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-card rounded-xl border border-border p-4">
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                <div className="relative flex-1 min-w-0">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search archived bids..."
                    value={bidSearch}
                    onChange={(e) => setBidSearch(e?.target?.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                  />
                </div>
                <select
                  value={bidStatusFilter}
                  onChange={(e) => setBidStatusFilter(e?.target?.value)}
                  className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                >
                  <option value="all">All Statuses</option>
                  <option value="Draft">Draft</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Converted to Project">Converted to Project</option>
                </select>
                {(bidSearch || bidStatusFilter !== 'all') && (
                  <button
                    onClick={() => { setBidSearch(''); setBidStatusFilter('all'); }}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors whitespace-nowrap"
                  >
                    <Icon name="X" size={14} />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Bids Table */}
            {bidsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={24} className="animate-spin text-accent mr-2" />
                <span className="text-muted-foreground">Loading archived bids...</span>
              </div>
            ) : bidsError ? (
              <div className="flex items-center gap-2 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{bidsError}</span>
                <button onClick={loadArchivedBids} className="ml-auto underline text-xs">Retry</button>
              </div>
            ) : filteredBids?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Icon name="Archive" size={24} className="text-muted-foreground" />
                </div>
                <h3 className="text-base font-medium text-foreground mb-1">No archived bids</h3>
                <p className="text-sm text-muted-foreground">
                  {bidSearch || bidStatusFilter !== 'all' ?'No bids match your filters.' :'Bids you archive will appear here.'}
                </p>
              </div>
            ) : (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        {[
                          { key: 'bidNumber', label: 'Bid #' },
                          { key: 'customerName', label: 'Customer' },
                          { key: 'projectName', label: 'Project Name' },
                          { key: 'totalAmount', label: 'Total Amount' },
                          { key: 'status', label: 'Status at Archive' },
                          { key: 'updatedAt', label: 'Archived On' },
                        ]?.map((col) => (
                          <th key={col?.key} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            <button
                              onClick={() => handleBidSort(col?.key)}
                              className="flex items-center hover:text-foreground transition-colors"
                            >
                              {col?.label}
                              <SortIcon col={col?.key} currentSort={bidSort} />
                            </button>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredBids?.map((bid) => (
                        <tr key={bid?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap">
                            {bid?.bidNumber}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {bid?.customerName}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground">
                            {bid?.projectName}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {bid?.totalAmount ? formatCurrency(bid?.totalAmount) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={bid?.preArchiveStatus || bid?.status} />
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(bid?.updatedAt || bid?.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnarchiveBid(bid)}
                              disabled={unarchivingBidId === bid?.id}
                              iconName={unarchivingBidId === bid?.id ? undefined : 'ArchiveRestore'}
                              iconPosition="left"
                              iconSize={14}
                            >
                              {unarchivingBidId === bid?.id ? 'Restoring...' : 'Unarchive'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2 border-t border-border bg-muted/20">
                  <p className="text-xs text-muted-foreground">
                    {filteredBids?.length} archived bid{filteredBids?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Archive;
